import { type ReactElement } from "react";
import { Flex, Row, Col, Space } from "antd";
import { createSearchParams, useSearchParams } from "react-router-dom";
import { EditButton } from "../../../../shared/layout/EditButton";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { type BiologicalProductDetails } from "../../../new-trail/types";
import { getProductUnit } from "../../utils";
import { ReactComponent as TankVolumeIcon } from "../../../../assets/images/drop.svg";
import { useProtocolsRouter } from "../../../../shared/custom-hooks/useProtocolsRouter";

interface BiologicalSummaryInterface {
  biologicals: BiologicalProductDetails | null;
}

const BiologicalProductSummaryCard = ({
  biologicals,
}: BiologicalSummaryInterface): ReactElement => {
  const { t } = useTranslation();
  const { router: protocolsRouter } = useProtocolsRouter();
  const [searchParams] = useSearchParams();
  const protocolId: string | null = searchParams.get("protocol_id");

  return (
    <Flex
      gap={32}
      justify="space-between"
      align="start"
      className="summay-content"
    >
      <Flex
        gap={16}
        align="center"
        justify="start"
        className="summary-content-title"
      >
        <div className="summary-card-title">
          {formatTranslation(t("createProtocol.summary.biological"))}
        </div>
        <EditButton
          className="summary"
          from="biologicals"
          onClick={() => {
            if (protocolId) {
              protocolsRouter.goToProtocolsEditPage(
                createSearchParams({
                  protocol_id: protocolId,
                  type: "biological",
                }).toString(),
                protocolId ?? "",
                "biological"
              );
            } else {
              protocolsRouter.goToProtocolsCreatePage(
                createSearchParams({ type: "biological" }).toString(),
                "biological"
              );
            }
          }}
        />
      </Flex>
      <Flex
        vertical
        gap={24}
        justify="start"
        className="summary-details-info-container"
      >
        {biologicals?.treatments?.map((item: any, index: number) => (
          <Flex
            vertical
            key={index}
            gap={16}
            justify="start"
            className="summary-treatment-info"
          >
            <label className="summary-card-static-title">
              {formatTranslation(
                t("createProtocol.products.biologicals.product.treatmentTitle")
              )}{" "}
              {index + 1}
            </label>
            <div className="treatement-summary">
              <Row className="products-header">
                <Col span={12}>
                  <p className="summary-card-static-text-seeds">
                    {formatTranslation(t("createProtocol.products.rightTilte"))}
                  </p>
                </Col>
                <Col span={12}>
                  <p className="summary-card-static-text-seeds">
                    {formatTranslation(
                      t("createProtocol.summary.biological.productRate")
                    )}
                  </p>
                </Col>
              </Row>

              {item?.products?.map((product: any, index: number) => (
                <Row key={index} className="products-row">
                  <Col span={12}>
                    <p className="summary-card-dynamic-protocol-text">
                      {product?.variety}
                    </p>
                  </Col>
                  <Col span={12}>
                    <p className="summary-card-dynamic-protocol-text">
                      {product?.rate} {getProductUnit(product?.measure_unit)}
                    </p>
                  </Col>
                </Row>
              ))}
            </div>
            <Flex
              gap={24}
              justify="between"
              className="summary-card-protocol-info"
            >
              <Flex
                vertical
                gap={8}
                justify="center"
                className="summary-card-protocol-info"
              >
                <label className="summary-card-static-text">
                  {formatTranslation(
                    t("createProtocol.products.biologicals.product.tankMixRate")
                  )}
                </label>
                <div className="biological-summary-card-dynamic-protocol-text">
                  <TankVolumeIcon className="tank-icon" />
                  <p className="summary-card-dynamic-protocol-text">
                    <b>{formatTranslation(t("createProtocol.products.min"))}</b>{" "}
                    {item?.tank_mix_rate?.min} L/ha -{" "}
                    <b>{formatTranslation(t("createProtocol.products.max"))}</b>{" "}
                    {item?.tank_mix_rate?.max} L/ha
                  </p>
                </div>
              </Flex>
              <Flex
                vertical
                gap={4}
                justify="center"
                className="summary-card-protocol-info"
              >
                <label className="summary-card-static-text">
                  {formatTranslation(
                    t(
                      "createProtocol.products.biologicals.product.instructions"
                    )
                  )}
                </label>
                <p className="summary-card-dynamic-protocol-text">
                  {item?.instructions}
                </p>
              </Flex>
            </Flex>
          </Flex>
        ))}

        <Space direction="vertical" size={24}>
          <div>
            {" "}
            <h6 className="summary-card-static-title">
              {formatTranslation(t("createProtocol.summary.title5"))}
            </h6>
          </div>

          {biologicals?.applications?.length === 0 ? (
            <p className="summary-card-dynamic-protocol-text">
              {formatTranslation(t("createProtocol.summary.noData"))}
            </p>
          ) : (
            biologicals?.applications?.map((item: any, index: number) => (
              <Flex vertical gap={4} justify="center" key={index}>
                <label className="summary-card-static-text">
                  {formatTranslation(
                    t("createProtocol.summary.biological.application")
                  )}{" "}
                  {index + 1}
                </label>
                <p className="summary-card-dynamic-protocol-text">
                  {item?.description}
                </p>
              </Flex>
            ))
          )}
        </Space>
      </Flex>
    </Flex>
  );
};
export default BiologicalProductSummaryCard;
